// Feature #325 — My Assessments: everything the signed-in user has in flight.
//
// The page is deliberately thin. What matters here is the ROW CONTRACT, not
// the layout: `SmMyAssessmentRow` carries a `kind` discriminator so #326's
// requested peer assessments — and later, submitted history — join this list
// without a second endpoint, a second type, or a second migration. Render by
// `kind` and new kinds slot in; branch on "is it a draft" and they won't.
//
// Visible to every authenticated user. Every other Skills Management child
// except My Skills is gated to sm_manager/sm_admin, so the obvious move of
// copying a neighbouring route in App.tsx would hide this from exactly the
// people it exists for — anyone can hold a draft self-assessment.
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDateTime } from '@/utils/dateFormat'
import { discardDraft, getMyAssessments, SmMyAssessmentRow } from '@/api/sm'

const KIND_LABEL: Record<string, string> = {
  draft: 'Draft',
  peer_request: 'Requested',
  submitted: 'Submitted',
}

const MyAssessments: React.FC = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<SmMyAssessmentRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = () =>
    getMyAssessments()
      .then(setRows)
      .catch((e) =>
        setError(e?.response?.data?.detail || e?.message || 'Failed to load assessments')
      )

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDiscard = async (row: SmMyAssessmentRow) => {
    if (!row.draft_id) return
    if (
      !window.confirm(
        `Discard the saved assessment for ${row.subject_display_name}? ` +
          'The answers already saved will be deleted.'
      )
    )
      return
    setBusy(row.draft_id)
    try {
      await discardDraft(row.draft_id)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to discard the draft')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <PageHeader title="My Assessments" />
      <div style={{ padding: '24px', display: 'grid', gap: '20px', maxWidth: '900px' }}>
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#b91c1c',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}
        {!rows && !error && (
          <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>
        )}

        {rows && rows.length === 0 && (
          <p style={{ color: 'var(--color-dark-grey)' }}>
            Nothing in progress. Assessments you save without submitting will appear
            here so you can finish them later.
          </p>
        )}

        {rows && rows.length > 0 && (
          <div style={{ display: 'grid', gap: '10px' }}>
            {rows.map((row) => (
              <div
                key={row.draft_id || row.request_id || `${row.kind}-${row.subject_user_id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderLeft: '4px solid var(--color-magenta)',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>
                    {row.assessment_type === 'self'
                      ? 'Self-assessment'
                      : `Peer assessment — ${row.subject_display_name}`}
                    {/* Feature #345 — say so up front. Somebody who opens a
                        blind form expecting to see the current ratings will
                        read their absence as a bug. */}
                    {row.request_id && !row.show_current_ratings && (
                      <span
                        title="You will not be shown their existing ratings — an independent view has been asked for."
                        style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: 'var(--color-dark-grey)',
                          border: '1px solid var(--color-light-grey)',
                          borderRadius: '10px',
                          padding: '1px 9px',
                        }}
                      >
                        blind
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-dark-grey)',
                      marginTop: '3px',
                    }}
                  >
                    {/* Feature #346 — a requested row leads with what is being
                        asked. "0 skills answered" is the right thing to say
                        about a draft and a useless thing to say about a
                        request nobody has opened yet. */}
                    {row.kind === 'peer_request'
                      ? row.scope_skill_names.join(', ') || 'no skills named'
                      : `${row.skills_answered} skill(s) answered · last saved ${formatDateTime(
                          row.updated_at
                        )}`}
                    {row.requested_by_display_name &&
                      ` · requested by ${row.requested_by_display_name}`}
                    {row.due_at && ` · due ${formatDateTime(row.due_at)}`}
                  </div>
                  {row.note && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--color-dark-grey)',
                        marginTop: '4px',
                        fontStyle: 'italic',
                      }}
                    >
                      “{row.note}”
                    </div>
                  )}
                </div>
                <span
                  style={{
                    fontSize: '11px',
                    color: 'var(--color-dark-grey)',
                    border: '1px solid var(--color-light-grey)',
                    borderRadius: '10px',
                    padding: '1px 9px',
                    flexShrink: 0,
                  }}
                >
                  {KIND_LABEL[row.kind] || row.kind}
                </span>
                <button
                  type="button"
                  onClick={() => navigate(row.resume_url)}
                  style={{
                    padding: '7px 16px',
                    backgroundColor: 'var(--color-magenta)',
                    color: 'var(--color-white)',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                    flexShrink: 0,
                  }}
                >
                  {row.kind === 'peer_request' ? 'Start' : 'Resume'}
                </button>
                {row.kind === 'draft' && (
                  <button
                    type="button"
                    onClick={() => handleDiscard(row)}
                    disabled={busy === row.draft_id}
                    title="Discard this draft"
                    style={{
                      padding: '7px 12px',
                      backgroundColor: 'transparent',
                      color: 'var(--color-dark-grey)',
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: busy === row.draft_id ? 'wait' : 'pointer',
                      fontFamily: 'var(--font-family)',
                      flexShrink: 0,
                    }}
                  >
                    Discard
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyAssessments
